import React, { useEffect, useRef, useContext } from "react";
import {
  ActionButton,
  HotelPriceAndRewardsDisplay,
  Icon,
  IconName,
  CorpPolicyBanner, isSpendAndGetOffer,
} from "halifax";
import { RouteComponentProps } from "react-router-dom";
import { DesktopListingSummaryBoxConnectorProps } from "./container";
import { CalendarPickerButton } from "../../../search/components/PremierCollectionSearchControl/components/CalendarPickerButton";
import { Box, Typography } from "@material-ui/core";
import "./styles.scss";
import * as textConstants from "./textConstants";
import { getEarnMultiplier } from "../../../rewards/utilities";
import clsx from "clsx";
import { OccupancySelection } from "../../../search/components/PremierCollectionSearchControl/components/OccupancySelection";
import {
  ListingCollectionEnum,
  RestrictedToProductsEnum,
  SelectedTravelOfferScreen,
} from "redmond";
import { goToVacationRentalsCheckout } from "../../../shop/utils/queryStringHelpers";
import { AvailabilityResultEnum } from "redmond/apis/tysons/vacation-rentals";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { ShopErrorCta } from "../ShopErrorCta";
import { IShopErrorCtaVariant } from "../ShopErrorCta/component";
import {
  isDateSelectionErrorType,
  isOccupancyErrorType,
} from "../ShopErrorCta/constants";
import { ShopErrorButton } from "../ShopErrorButton";
import { useShowPolicyBanner, isCorpTenant } from "@capone/common";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";

export interface DesktopListingSummaryBoxProps
  extends DesktopListingSummaryBoxConnectorProps,
    RouteComponentProps {
  isSkeleton?: boolean;
  isVRForPremiumCardHoldersEnabled: boolean;
  isVRCalendarEnabled?: boolean;
  updatedBenefits?: boolean;
  largestValueAccount?: any;
}

export const DesktopListingSummaryBox = (
  props: DesktopListingSummaryBoxProps
) => {
  const {
    nightCount,
    isSkeleton,
    rewardsKey,
    listing,
    checkinDate,
    checkoutDate,
    adultsCount,
    childrenCount,
    fetchVacationRentalShop,
    history,
    petsCount,
    isVRForPremiumCardHoldersEnabled,
    isVRCalendarEnabled,
    shopOffer,
    updatedBenefits = false,
    largestValueAccount,
    listPrice,
  } = props;

  const isFirstUpdate = useRef<boolean>(true);
  const expState = useExperiments();

  const contextData = useContext(ClientContext);
  const policies = contextData?.policies;
  const showPolicyBanner = useShowPolicyBanner(policies, "home");
  const tenant = config.TENANT;

  const hasPolicyCompliance = listing && "corporateTravel" in listing;

  const isInPolicy = Boolean(
    listing?.corporateTravel?.policyCompliance?.isInPolicy
  );


  const isXmodRedesign = getExperimentVariant(
    expState.experiments,
    XMOD_V1_EXPERIMENT
  ).startsWith(AVAILABLE);

  useEffect(() => {
    if (
      checkinDate &&
      checkoutDate &&
      (adultsCount || childrenCount || petsCount)
    ) {
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else {
        fetchVacationRentalShop(
          history,
          {
            forceCallVRAvailability: true,
            fetchListingCalendar: isVRCalendarEnabled,
          },
          isVRForPremiumCardHoldersEnabled
        );
      }
    }
  }, [checkinDate, checkoutDate, adultsCount, childrenCount, petsCount]);

  const available =
    listing?.availability?.AvailabilityResult ===
    AvailabilityResultEnum.Available;

  // Price consistency: prefer the /homes/availability price (honors all-in pricing).
  // While it's still loading show a skeleton; if it failed (null) fall back to the
  // shop price so the headline never blocks on availability.
  const priceLoading = listPrice === undefined;
  const availabilityPrice = listPrice ?? listing?.availability.rate?.price;
  // halifax's total-price line ignores `isSkeleton`, so pass no price while loading:
  // the nightly PriceSkeleton draws both nightly+total bars and the total self-hides,
  // keeping the (possibly different) shop total from leaking through the skeleton.
  const showPriceSkeleton = isSkeleton || priceLoading;

  const failureReason = listing?.availability?.reasons?.[0]?.reason?.Reason;
  const isDateSelectionError = isDateSelectionErrorType(failureReason);
  const isOccupanySelectionError = isOccupancyErrorType(failureReason);

  const getUpdatedBenefitsList = (
    variant?: "default" | "lifestyle-collection",
    currency?: string
  ) => {
    const isLifestyleCollection = variant === "lifestyle-collection";
    const { multiplier, currency: currencyText } = getEarnMultiplier(currency);
    return (
      <Box className="updated-benefits-list">
        <Box className="benefit-item">
          <Icon name={IconName.TrophyVRShopBenefits} className="benefit-icon" />
          <Box className="benefit-content">
            <Typography className="benefit-title">Earn 5{multiplier} {currencyText}</Typography>
            <Typography className="benefit-subtitle">
              On every vacation rental booking from the
              {isLifestyleCollection ? " Lifestyle" : " Premier"} Collection
            </Typography>
          </Box>
        </Box>
        <Box className="benefit-item">
          <Icon name={IconName.GiftVRShopBenefits} className="benefit-icon" />
          <Box className="benefit-content">
            <Typography className="benefit-title">
              {isLifestyleCollection ? "$50" : "$100"} experience credit
            </Typography>
            <Typography className="benefit-subtitle">
              Use towards activities, dining, and more at your destination
            </Typography>
          </Box>
        </Box>
        <Box className="benefit-item">
          <Icon name={IconName.ClockVRShopBenefits} className="benefit-icon" />
          <Box className="benefit-content">
            <Typography className="benefit-title">
              Early check-in and late checkout
            </Typography>
            <Typography className="benefit-subtitle">when available</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      className={clsx("listing-reserve-summary-container", {
        "lifestyle-collection":
          listing?.listing.listingCollection ===
          ListingCollectionEnum.Lifestyle,
        "premier-collection":
          listing?.listing.listingCollection === ListingCollectionEnum.Premier,
        xmod: isXmodRedesign,
        "with-policy-banner": showPolicyBanner && hasPolicyCompliance,
        "out-of-policy": hasPolicyCompliance && !isInPolicy,
      })}
    >
      {listing?.listing.listingCollection ===
      ListingCollectionEnum.Lifestyle ? (
        <Icon name={IconName.LifestyleCollectionRibbon} />
      ) : (
        <Icon name={IconName.PremierCollectionRibbon} />
      )}
      {showPolicyBanner && isCorpTenant(tenant) && hasPolicyCompliance && (
        <CorpPolicyBanner
          variant="base"
          productType="home"
          corporateTravel={listing?.corporateTravel}
          limit={policies?.hotels?.policies?.[0]?.maxPricePerNight}
        />
      )}
      <Box className="listing-summary">
        {available ? (
          <HotelPriceAndRewardsDisplay
            lodgingPrice={showPriceSkeleton ? undefined : availabilityPrice}
            nightCount={nightCount ?? 0}
            isSkeleton={showPriceSkeleton}
            rewardsKey={rewardsKey}
            variant={"default"}
          />
        ) : (
          <ShopErrorCta variant={IShopErrorCtaVariant.DESKTOP} />
        )}
        <CalendarPickerButton
          classes={[
            "room-calendar-button",
            isDateSelectionError ? "error" : "",
          ]}
          hideSeparator
        />
        <OccupancySelection
          maxOccupancy={listing?.listing.terms.maxOccupancy}
          showErrorStyles={isOccupanySelectionError}
        />

        {available ? (
          <ActionButton
            className="b2b"
            onClick={() => {
              goToVacationRentalsCheckout({ history });
            }}
            message={
              <Typography variant="h2" className="button-text">
                {textConstants.RESERVE_CTA_TEXT}
              </Typography>
            }
          />
        ) : (
          <ShopErrorButton />
        )}
      </Box>

      <Box
        className={clsx("collection-shop-benefits", {
          "updated-benefits": updatedBenefits,
        })}
      >
        <Box className="collection-benefits-title">
          {!updatedBenefits && <Icon name={IconName.StarOutline} />}
          <Typography className="collection-benefits-title-text">
            {listing?.listing.listingCollection ===
            ListingCollectionEnum.Lifestyle
              ? textConstants.LC_BENEFITS_TITLE
              : textConstants.BENEFITS_TITLE}
          </Typography>
        </Box>

        {!!updatedBenefits ? (
          getUpdatedBenefitsList(
            listing?.listing.listingCollection ===
              ListingCollectionEnum.Lifestyle
              ? "lifestyle-collection"
              : "default",
            largestValueAccount?.rewardsBalance?.currencyDescription ||
              largestValueAccount?.rewardsBalance?.currency
          )
        ) : (
          <Typography className="collection-benefits-list">
            {textConstants.getBenefitsList(
              listing?.listing.listingCollection ===
                ListingCollectionEnum.Lifestyle
                ? "lifestyle-collection"
                : "default"
            )}
          </Typography>
        )}
        {!updatedBenefits && (
          <Box className="additional-benefits">
            <Icon name={IconName.GiftOutline} />
            <Typography className="additional-benefits-text">
              {textConstants.ADDITIONAL_BENEFITS}
            </Typography>
          </Box>
        )}
      </Box>

      {!isSpendAndGetOffer(shopOffer?.descriptions) && (
        <TravelWalletDetailsBanner
          offer={shopOffer!}
          showButton={false}
          className="home-shop-offer"
          screen={SelectedTravelOfferScreen.PREMIUM_STAYS_DETAILS}
          restrictedToProduct={RestrictedToProductsEnum.Homes}
          {...props}
        />
      )}
    </Box>
  );
};
